import { useState } from "react";
import { Link } from "react-router-dom";
import { SUPPORT_EMAIL } from "../lib/runtimeConfig.js";
import { createTripRequest } from "../lib/travelApi.js";
import Seo from "../components/Seo.jsx";

// Shared SEO for both the form and the success view of this route.
const PLAN_SEO = (
  <Seo
    title="Plan a Trip — 513Travel"
    description="Tell 513Travel where you want to go and our travel concierge will prepare options, clear pricing and next steps."
    path="/plan-a-trip"
  />
);

/*
 * Plan a Trip — customer travel-request form.
 *
 * Phase 2B: wired to the live backend. The form collects a complete request
 * into a single `form` object and hands it to createTripRequest() (in
 * src/lib/travelApi.js), which POSTs to https://api.513travel.com/travel/requests
 * and returns the server-issued reference. On failure we show a friendly error
 * with an email fallback and never fabricate a reference.
 */

const SERVICES = [
  "Flights",
  "Stays",
  "Visa Support",
  "Tours & Experiences",
  "Airport Transfer",
  "Full Travel Package",
  "Custom Trip",
];

const TRAVEL_CLASSES = [
  "Economy",
  "Premium Economy",
  "Business",
  "First Class",
  "Luxury",
  "Budget-friendly",
  "Not sure yet",
];

const CURRENCIES = ["NGN", "USD", "GHS", "KES", "ZAR", "EUR", "GBP", "Other"];

const LANGUAGES = [
  "English",
  "French",
  "Arabic",
  "Swahili",
  "Yoruba",
  "Hausa",
  "Igbo",
  "Portuguese",
];

// Launch-market dial codes first, then common diaspora markets. "Other" lets
// the traveller type a full international number themselves (no prefix added).
const COUNTRY_CODES = [
  { name: "Nigeria", dial: "+234" },
  { name: "Ghana", dial: "+233" },
  { name: "Kenya", dial: "+254" },
  { name: "Rwanda", dial: "+250" },
  { name: "South Africa", dial: "+27" },
  { name: "Tanzania", dial: "+255" },
  { name: "Uganda", dial: "+256" },
  { name: "Ethiopia", dial: "+251" },
  { name: "Senegal", dial: "+221" },
  { name: "Côte d'Ivoire", dial: "+225" },
  { name: "Cameroon", dial: "+237" },
  { name: "Morocco", dial: "+212" },
  { name: "Egypt", dial: "+20" },
  { name: "United Arab Emirates", dial: "+971" },
  { name: "United Kingdom", dial: "+44" },
  { name: "United States", dial: "+1" },
  { name: "France", dial: "+33" },
  { name: "Other", dial: "other" },
];

const MOODS = [
  "Relaxation",
  "Business",
  "Honeymoon",
  "Family",
  "Culture",
  "Adventure",
  "Luxury",
  "Religious/Pilgrimage",
  "Medical",
  "Education",
  "Other",
];

const NEXT_STEPS = [
  { num: "01", text: "We review your request" },
  { num: "02", text: "We prepare suitable options" },
  { num: "03", text: "You confirm and pay securely" },
  { num: "04", text: "We support you before and during travel" },
];

const INITIAL_FORM = {
  services: [],
  fromLocation: "",
  destination: "",
  departureDate: "",
  returnDate: "",
  flexibleDates: "no",
  adults: 1,
  children: 0,
  travelClass: "",
  budget: "",
  currency: "NGN",
  language: "English",
  mood: "",
  fullName: "",
  email: "",
  countryCode: "+234",
  whatsappNumber: "",
  residence: "",
  notes: "",
  consent: false,
};

// Combine the selected dial code with the typed number into one E.164-ish
// string for backend submission. "Other" carries no prefix — the traveller
// types the full international number themselves.
function buildFullWhatsApp(countryCode, number) {
  const trimmed = String(number || "").trim();
  if (!trimmed) return "";
  if (!countryCode || countryCode === "other") return trimmed;
  return `${countryCode} ${trimmed}`;
}

export default function PlanTrip() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { reference } — server-issued
  const [submitError, setSubmitError] = useState("");

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const toggleService = (service) => {
    setForm((f) => {
      const has = f.services.includes(service);
      return {
        ...f,
        services: has
          ? f.services.filter((s) => s !== service)
          : [...f.services, service],
      };
    });
    if (errors.services) setErrors((e) => ({ ...e, services: undefined }));
  };

  function validate() {
    const next = {};
    if (form.services.length === 0) next.services = "Pick at least one service.";
    if (!form.destination.trim()) next.destination = "Where would you like to go?";
    if (!form.departureDate) next.departureDate = "Add a departure date.";
    if (Number(form.adults) < 1) next.adults = "At least one traveller is required.";
    if (!form.fullName.trim()) next.fullName = "Please tell us your name.";
    if (!form.email.trim() && !form.whatsappNumber.trim()) {
      next.contact = "Add an email or WhatsApp number so we can reach you.";
    }
    if (!form.consent) next.consent = "Please confirm to continue.";
    return next;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      // Surface the first error for keyboard/screen-reader + scroll users.
      const firstEl = document.querySelector("[data-invalid='true']");
      firstEl?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = {
        ...form,
        fullWhatsAppNumber: buildFullWhatsApp(form.countryCode, form.whatsappNumber),
      };
      const { reference } = await createTripRequest(payload);
      // Only show a reference the server actually issued — never fabricate one.
      if (!reference) throw new Error("missing-reference");
      setResult({ reference });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setSubmitError(
        err && err.userMessage
          ? err.userMessage
          : "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  function resetForm() {
    setForm(INITIAL_FORM);
    setErrors({});
    setResult(null);
    setSubmitError("");
  }

  // ---------- Success state ----------
  if (result) {
    return (
      <div className="page">
        {PLAN_SEO}
        <section className="page-hero">
          <div className="container">
            <div className="eyebrow">Plan a Trip</div>
            <h1>Your travel request has been prepared.</h1>
            <p className="lede">
              Thank you. Please quote your reference if you contact us by email
              or WhatsApp. Our team will review your request and follow up with
              options and next steps.
            </p>

            <div className="info-card success-card">
              <span className="success-tick" aria-hidden="true">✓</span>
              <p className="success-label">Your request reference</p>
              <p className="success-ref">{result.reference}</p>
              <p className="success-note">
                Keep this reference handy — you can track it from the
                Track Trip page.
              </p>
            </div>

            <div className="page-actions">
              <button type="button" className="btn gold" onClick={resetForm}>
                Submit another request
              </button>
              <Link to="/" className="btn outline">Back to home</Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ---------- Form state ----------
  return (
    <div className="page">
      {PLAN_SEO}
      <section className="page-hero">
        <div className="container">
          <div className="eyebrow">Plan a Trip</div>
          <h1>Tell us where you want to go</h1>
          <p className="lede">
            Share a few details and our travel concierge will prepare options,
            clear pricing and next steps. Fast response, no obligation.
          </p>
        </div>
      </section>

      <section className="section plan-section">
        <div className="container plan-layout">
          {/* ===== Main form ===== */}
          <form className="trip-form" onSubmit={handleSubmit} noValidate>
            {Object.keys(errors).length > 0 && (
              <p className="form-error-top" role="alert">
                Please check the highlighted fields below.
              </p>
            )}

            {/* 1. Services */}
            <fieldset className="trip-section">
              <legend>What do you need?</legend>
              <p className="trip-hint">Select all that apply.</p>
              <div
                className="choice-grid"
                data-invalid={errors.services ? "true" : undefined}
              >
                {SERVICES.map((s) => {
                  const active = form.services.includes(s);
                  return (
                    <button
                      type="button"
                      key={s}
                      className={`choice-card ${active ? "is-selected" : ""}`}
                      aria-pressed={active}
                      onClick={() => toggleService(s)}
                    >
                      <span className="choice-check" aria-hidden="true">
                        {active ? "✓" : ""}
                      </span>
                      {s}
                    </button>
                  );
                })}
              </div>
              {errors.services && <p className="field-error">{errors.services}</p>}
            </fieldset>

            {/* 2. Travel details */}
            <fieldset className="trip-section">
              <legend>Travel details</legend>
              <div className="form-grid">
                <div className="field">
                  <label htmlFor="from">From (city / country)</label>
                  <input id="from" type="text" value={form.fromLocation}
                    onChange={(e) => set("fromLocation", e.target.value)}
                    placeholder="e.g. Lagos, Nigeria" />
                </div>
                <div className="field" data-invalid={errors.destination ? "true" : undefined}>
                  <label htmlFor="dest">Destination (city / country) *</label>
                  <input id="dest" type="text" value={form.destination}
                    onChange={(e) => set("destination", e.target.value)}
                    placeholder="e.g. Zanzibar, Tanzania" />
                  {errors.destination && <p className="field-error">{errors.destination}</p>}
                </div>
                <div className="field" data-invalid={errors.departureDate ? "true" : undefined}>
                  <label htmlFor="dep">Departure date *</label>
                  <input id="dep" type="date" value={form.departureDate}
                    onChange={(e) => set("departureDate", e.target.value)} />
                  {errors.departureDate && <p className="field-error">{errors.departureDate}</p>}
                </div>
                <div className="field">
                  <label htmlFor="ret">Return date</label>
                  <input id="ret" type="date" value={form.returnDate}
                    onChange={(e) => set("returnDate", e.target.value)} />
                </div>
              </div>

              <div className="field">
                <label>Flexible dates?</label>
                <div className="pill-group" role="group" aria-label="Flexible dates">
                  {["no", "yes"].map((v) => (
                    <button type="button" key={v}
                      className={`pill ${form.flexibleDates === v ? "is-selected" : ""}`}
                      aria-pressed={form.flexibleDates === v}
                      onClick={() => set("flexibleDates", v)}>
                      {v === "yes" ? "Yes, I'm flexible" : "No, fixed dates"}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-grid">
                <div className="field" data-invalid={errors.adults ? "true" : undefined}>
                  <label htmlFor="adults">Adults *</label>
                  <input id="adults" type="number" min="1" value={form.adults}
                    onChange={(e) => set("adults", e.target.value)} />
                  {errors.adults && <p className="field-error">{errors.adults}</p>}
                </div>
                <div className="field">
                  <label htmlFor="children">Children</label>
                  <input id="children" type="number" min="0" value={form.children}
                    onChange={(e) => set("children", e.target.value)} />
                </div>
              </div>

              <div className="field">
                <label>Travel class / style</label>
                <div className="pill-group" role="group" aria-label="Travel class or style">
                  {TRAVEL_CLASSES.map((c) => (
                    <button type="button" key={c}
                      className={`pill ${form.travelClass === c ? "is-selected" : ""}`}
                      aria-pressed={form.travelClass === c}
                      onClick={() => set("travelClass", c)}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            </fieldset>

            {/* 3. Budget and preferences */}
            <fieldset className="trip-section">
              <legend>Budget &amp; preferences</legend>
              <div className="form-grid">
                <div className="field">
                  <label htmlFor="budget">Estimated budget</label>
                  <input id="budget" type="text" inputMode="numeric" value={form.budget}
                    onChange={(e) => set("budget", e.target.value)}
                    placeholder="e.g. 2,000,000" />
                </div>
                <div className="field">
                  <label htmlFor="currency">Currency</label>
                  <select id="currency" value={form.currency}
                    onChange={(e) => set("currency", e.target.value)}>
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="language">Preferred language</label>
                  <select id="language" value={form.language}
                    onChange={(e) => set("language", e.target.value)}>
                    {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
              </div>

              <div className="field">
                <label>Travel mood</label>
                <div className="pill-group" role="group" aria-label="Travel mood">
                  {MOODS.map((m) => (
                    <button type="button" key={m}
                      className={`pill ${form.mood === m ? "is-selected" : ""}`}
                      aria-pressed={form.mood === m}
                      onClick={() => set("mood", m)}>
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            </fieldset>

            {/* 4. Customer details */}
            <fieldset className="trip-section">
              <legend>Your details</legend>
              <div className="form-grid">
                <div className="field" data-invalid={errors.fullName ? "true" : undefined}>
                  <label htmlFor="name">Full name *</label>
                  <input id="name" type="text" value={form.fullName}
                    onChange={(e) => set("fullName", e.target.value)} />
                  {errors.fullName && <p className="field-error">{errors.fullName}</p>}
                </div>
                <div className="field" data-invalid={errors.contact ? "true" : undefined}>
                  <label htmlFor="email">Email address</label>
                  <input id="email" type="email" value={form.email}
                    onChange={(e) => set("email", e.target.value)} />
                </div>
                <div className="field" data-invalid={errors.contact ? "true" : undefined}>
                  <label htmlFor="whatsapp">WhatsApp number</label>
                  <div className="phone-row">
                    <select
                      aria-label="Country dialing code"
                      className="phone-code"
                      value={form.countryCode}
                      onChange={(e) => set("countryCode", e.target.value)}
                    >
                      {COUNTRY_CODES.map((c) => (
                        <option key={c.name} value={c.dial}>
                          {c.dial === "other" ? "Other" : `${c.name} (${c.dial})`}
                        </option>
                      ))}
                    </select>
                    <input id="whatsapp" type="tel" value={form.whatsappNumber}
                      onChange={(e) => set("whatsappNumber", e.target.value)}
                      placeholder={form.countryCode === "other" ? "Full international number" : "801 234 5678"} />
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="residence">Country of residence</label>
                  <input id="residence" type="text" value={form.residence}
                    onChange={(e) => set("residence", e.target.value)} />
                </div>
              </div>
              {errors.contact && <p className="field-error">{errors.contact}</p>}
            </fieldset>

            {/* 5. Notes */}
            <fieldset className="trip-section">
              <legend>Anything else?</legend>
              <div className="field">
                <label htmlFor="notes">Tell us anything important about your trip</label>
                <textarea id="notes" rows={4} value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  placeholder="Occasion, must-haves, accessibility needs, preferred airlines or hotels…" />
              </div>
            </fieldset>

            {/* 6. Consent */}
            <div className={`consent-row ${errors.consent ? "is-invalid" : ""}`}
              data-invalid={errors.consent ? "true" : undefined}>
              <label className="consent-label">
                <input type="checkbox" checked={form.consent}
                  onChange={(e) => set("consent", e.target.checked)} />
                <span>
                  I understand this is a travel request and 513Travel will contact
                  me with options, pricing and next steps.
                </span>
              </label>
              {errors.consent && <p className="field-error">{errors.consent}</p>}
            </div>

            {submitError && (
              <p className="form-error-top" role="alert">
                {submitError} You can also email us at{" "}
                <a className="inline-link" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
              </p>
            )}

            <button type="submit" className="btn gold trip-submit" disabled={submitting}>
              {submitting ? "Submitting…" : "Submit Travel Request"}
            </button>
          </form>

          {/* ===== Sidebar ===== */}
          <aside className="plan-aside">
            <div className="aside-card">
              <h2>What happens next?</h2>
              <ol className="next-steps">
                {NEXT_STEPS.map((s) => (
                  <li key={s.num}>
                    <span className="next-num">{s.num}</span>
                    <span>{s.text}</span>
                  </li>
                ))}
              </ol>
              <p className="aside-note">
                Prefer to talk first? Email{" "}
                <a className="inline-link" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
